import React, { useState, useEffect } from "react";
import { Button, Dropdown, Input, MenuProps } from "antd";
import { DownOutlined, SettingOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { ATSIntegrationProps, ATSProvider } from "../../../types/Step0.types";
import atsIntegrationService from "../../../../../Service/ats.integration.service";
import ATSJobImportDrawer from "../ATSJobImportDrawer";
import { useAuth } from "../../../../../Context/AuthContext/AuthContext";
import "./index.scss";

const ATSIntegration: React.FC<ATSIntegrationProps> = ({
  onATSClick,
  onImport,
}) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>("");
  const [connectedApps, setConnectedApps] = useState<
    Array<{ appType: string; _id: string; name: string }>
  >([]);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ATSProvider | null>(
    null
  );

  const { user } = useAuth();

  const allATSProviders: readonly ATSProvider[] = [
    {
      key: "lever",
      label: "Lever",
      icon: "",
      isAvailable: true,
    },
    {
      key: "zoho",
      label: "Zoho Recruit",
      icon: "",
      isAvailable: true,
    },
  ] as const;

  // Fetch connected apps on mount
  useEffect(() => {
    const fetchConnectedApps = async () => {
      try {
        const response = await atsIntegrationService.getConnectedATSApps();
        if (response.success && response.data) {
          setConnectedApps(response.data);
        } else {
          console.error("Error fetching connected apps:", response.message);
        }
      } catch (error) {
        console.error("Error fetching connected apps:", error);
      }
    };

    fetchConnectedApps();
  }, []);

  // Map connected apps to providers, using dynamic names from API
  const atsProviders = connectedApps.map((app) => {
    // Find matching provider config or create one dynamically
    const providerConfig = allATSProviders.find((p) => p.key === app.appType);
    return {
      key: app.appType,
      label: app.name || app.appType, // Use name from API, fallback to appType
      icon: providerConfig?.icon || "",
      isAvailable: true,
    };
  });

  /**
   * Filter providers based on search
   */
  const filteredProviders = atsProviders.filter((provider) =>
    provider.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  /**
   * Handle ATS provider click
   * Opens drawer with job posts from selected provider
   */
  const handleATSClick = (provider: ATSProvider) => {
    // Call custom handler if provided
    if (onATSClick) {
      onATSClick(provider);
    }

    // Open drawer
    setSelectedProvider(provider);
    setIsDrawerVisible(true);
  };

  /**
   * Handle manage ATS click
   * Navigate to organization settings with apps tab
   */
  const handleManageATSClick = () => {
    navigate("/organization?tab=apps");
  };

  /**
   * Create dropdown menu items
   * Maps ATS providers to Ant Design menu items
   */
  const createMenuItems = (): MenuProps["items"] => {
    const providerItems: MenuProps["items"] = filteredProviders.map(
      (provider) => ({
        key: provider.key,
        label: (
          <div className="ats-menu-item">
            {provider.label}
          </div>
        ),
        onClick: () => handleATSClick(provider),
        disabled: !provider.isAvailable,
      })
    );

    const items: MenuProps["items"] = [
      {
        key: "search",
        label: (
          <Input
            placeholder="Search ATS"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="ats-search-input"
          />
        ),
        disabled: true,
      },
      { type: "divider" },
      ...providerItems,
      { type: "divider" },
    ];

    if (user?.role === "admin") {
      items.push({
        key: "manage-ats",
        label: (
          <div className="ats-manage-item">
            <SettingOutlined className="ats-manage-icon" />
            <span className="ats-manage-label">&nbsp;Manage ATS</span>
          </div>
        ),
        onClick: handleManageATSClick,
      });
    }

    return items;
  };

  const handleDrawerClose = () => {
    setIsDrawerVisible(false);
    setSelectedProvider(null);
  };

  const handleImport = (
    promptText: string,
    appJobId: string,
    appType: string
  ) => {
    if (onImport) {
      onImport(promptText, appJobId, appType);
    }
  };

  return (
    <>
      <div className="ats-integration">
        <Dropdown
          menu={{ items: createMenuItems() }}
          trigger={["click", "hover"]}
          placement="bottomLeft"
          overlayClassName="ats-dropdown-overlay"
        >
          <Button
            type="text"
            size="middle"
            className="ats-import-button"
            aria-label="Import from ATS systems"
          >
            Import from ATS
            <DownOutlined className="dropdown-arrow" />
          </Button>
        </Dropdown>
      </div>

      {selectedProvider &&
        (() => {
          const appType = selectedProvider.key;
          const connectedApp = connectedApps.find(
            (app) => app.appType === appType
          );
          const appName = connectedApp?.name || selectedProvider.label;

          return (
            <ATSJobImportDrawer
              visible={isDrawerVisible}
              onClose={handleDrawerClose}
              provider={selectedProvider}
              appName={appName}
              onImport={handleImport}
            />
          );
        })()}
    </>
  );
};

export default ATSIntegration;
